import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Card, 
  CardContent, 
  Grid2, 
  Avatar, 
  useTheme,
  Alert,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Person, 
  Visibility, 
  VisibilityOff, 
  Save, 
  Lock,
  Settings as SettingsIcon,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { getUserProfile, updateUserProfile, resetPassword } from '../actions/users';
import { useAlert } from '../contexts/AlertContext';
import Cookies from 'js-cookie';

const Settings = () => {
  const theme = useTheme();
  const { showAlert } = useAlert();
  const [userProfile, setUserProfile] = useState({
    name: '',
    picture_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const profile = await getUserProfile();
      setUserProfile(profile);
    } catch (error) {
      showAlert('Failed to fetch user profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setLoading(true);
      await updateUserProfile({
        name: userProfile.name
      });
      showAlert('Profile updated successfully!', 'success');
      // Update the user cookie with new name
      Cookies.set('user', userProfile.name, { expires: 7 });
    } catch (error) {
      showAlert('Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  // Check if password meets all requirements
  const isPasswordValid = (password) => {
    return validatePassword(password).length === 0;
  };

  // Handle password field blur
  const handlePasswordBlur = () => {
    if (passwordData.new_password && !isPasswordValid(passwordData.new_password)) {
      setShowPasswordRequirements(true);
    } else {
      setShowPasswordRequirements(false);
    }
  };

  // Handle password field focus
  const handlePasswordFocus = () => {
    setShowPasswordRequirements(false);
  };

  // Get password requirements message
  const getPasswordRequirementsMessage = () => {
    const password = passwordData.new_password;
    const requirements = [];
    
    if (password.length < 8) {
      requirements.push('at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      requirements.push('one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      requirements.push('one lowercase letter');
    }
    if (!/\d/.test(password)) {
      requirements.push('one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      requirements.push('one special character');
    }
    
    return requirements.join(', ');
  };

  const handlePasswordReset = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showAlert('New passwords do not match', 'error');
      return;
    }

    if (passwordData.current_password === passwordData.new_password) {
      showAlert('New password must be different from current password', 'error');
      return;
    }

    const passwordErrors = validatePassword(passwordData.new_password);
    if (passwordErrors.length > 0) {
      showAlert(`Password requirements not met: ${passwordErrors.join(', ')}`, 'error');
      return;
    }

    try {
      setLoading(true);
      await resetPassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      showAlert('Password updated successfully!', 'success');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      // Show server-specific error message
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update password';
      showAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      {/* Header */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 6,
        p: { xs: 2, sm: 4 }
      }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 2,
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <SettingsIcon sx={{ color: theme.palette.primary.main }} />
          Settings
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}
        >
          Manage your account preferences and security
        </Typography>
      </Box>

      <Grid2 container spacing={3} maxWidth="800px" sx={{ mx: 'auto' }}>
        {/* Profile Settings */}
        <Grid2 size={12} md={6}>
          <Card sx={{
            height: '100%',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Person sx={{ mr: 2, color: theme.palette.primary.main }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Profile Settings
                </Typography>
              </Box>

              {/* Profile Picture Display */}
              {userProfile.picture_url && (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar
                    src={userProfile.picture_url}
                    alt={userProfile.name}
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      mx: 'auto',
                      mb: 2,
                      border: `3px solid ${theme.palette.primary.main}`
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Profile picture from Google account
                  </Typography>
                </Box>
              )}

              <TextField
                label="Name"
                name="name"
                value={userProfile.name}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              {/* {!userProfile.picture_url && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No profile picture available. Connect your Google account to add a profile picture.
                </Alert>
              )} */}

              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleProfileUpdate}
                disabled={loading}
                fullWidth
                sx={{ 
                  mt: 3,
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Save Profile Changes
              </Button>
            </CardContent>
          </Card>
        </Grid2>

        {/* Password Settings */}
        <Grid2 size={12} md={6}>
          <Card sx={{
            height: '100%',
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)',
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Lock sx={{ mr: 2, color: theme.palette.primary.main }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Password Settings
                </Typography>
              </Box>

              <TextField
                label="Current Password"
                name="current_password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                label="New Password"
                name="new_password"
                type={showNewPassword ? 'text' : 'password'}
                value={passwordData.new_password}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                onFocus={handlePasswordFocus}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              {/* Password Requirements Alert - Only show when user moves out of field */}
              {showPasswordRequirements && (
                <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
                  Password must contain: {getPasswordRequirementsMessage()}
                </Alert>
              )}

              <TextField
                label="Confirm New Password"
                name="confirm_password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={passwordData.confirm_password}
                onChange={handlePasswordChange}
                fullWidth
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <Button
                variant="contained"
                color="primary"
                startIcon={<Lock />}
                onClick={handlePasswordReset}
                disabled={loading || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password || !isPasswordValid(passwordData.new_password)}
                fullWidth
                sx={{ 
                  mt: 3,
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Update Password
              </Button>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default Settings; 